/**
 * publish-branch.js — which publish route, and why.
 *
 * Contracts §5. A pure function, on purpose: "Publish in place, no modal" is
 * UNSAFE for a first publish, and the thing that decides whether a modal is
 * skipped is the last piece of code that should be hard to test.
 *
 * ── What the modal owns, and why bypassing it is the danger ───────────────
 * `publish-site-modal.js` is not a confirmation dialog. It owns slug choice
 * with live availability, the static-incompatibility explanation, claimed-site
 * management (change address, unpublish), the known-issue reveal-then-publish
 * recognition gate, and error/cancel handling. Every one of those is the ONLY
 * place its job is done.
 *
 * So this module never decides "skip the safety flow". It decides "is there a
 * decision left for the user to make". When there is not — the address is
 * already claimed, the packs are compatible, the tab can write — publishing is
 * a republish of a site that already exists at an address the user already
 * chose, and putting a dialog in front of that is ceremony.
 *
 * ── Why it returns a REASON, not a boolean ────────────────────────────────
 * Three of the five routes end in a message to the user, and a caller that has
 * to re-derive which one would be re-implementing the decision. The reason is
 * also what the tests assert on, so a route change is visible rather than
 * inferred from which branch happened to run.
 */

/**
 * @typedef {object} PublishContext
 * @property {boolean} signedIn — a REAL account (not the single-user fallback)
 * @property {string|null} projectId
 * @property {boolean} writable — `store.isWritable()`; a read-only tab may be
 *   displaying older state than the cloud head
 * @property {Array<{key: string, label: string}>} incompatible — the result of
 *   `staticIncompatibleFeatures(state.features)`. Passed IN rather than derived
 *   here so this module imports nothing and stays trivially loadable.
 * @property {object|null} site — the claimed site row, or null if the project
 *   has never been published. `undefined` means "not read yet" and is NOT the
 *   same as null — see below.
 */

/** Every route this can return. Exhaustive by construction; the caller switches on it. */
export const PUBLISH_ROUTES = Object.freeze([
  'auth', // not signed in
  'no-project', // signed in, but nothing to publish
  'read-only', // another tab holds the write lock
  'modal', // there is a decision left for the user
  'direct' // republish in place
]);

/**
 * @param {PublishContext} ctx
 * @returns {{route: string, reason: string}}
 */
export function decidePublishRoute(ctx = {}) {
  const { signedIn, projectId, writable, incompatible, site } = ctx;

  if (!signedIn) {
    return { route: 'auth', reason: 'Sign in to publish your site' };
  }
  if (!projectId) {
    return { route: 'no-project', reason: 'Save this project before publishing it' };
  }
  if (writable === false) {
    return {
      route: 'read-only',
      reason: 'Publishing is available in the editing tab — this tab is read-only'
    };
  }

  // Backend-dependent packs: the modal is the only surface that EXPLAINS the
  // block, and "your contact form would post into the void" is not a toast.
  if (Array.isArray(incompatible) && incompatible.length > 0) {
    return { route: 'modal', reason: 'Some features need a server' };
  }

  // `undefined` is "the site read has not happened or failed", and it is
  // deliberately NOT treated as "never published". Reading a failed lookup as
  // "no site yet" would send a user with a live site into the slug picker and
  // invite them to claim a second address — the same shape as the lifecycle
  // rule that a read-path 404 is not a deletion.
  if (site === undefined) {
    return { route: 'modal', reason: 'Could not confirm the current address' };
  }
  if (!site || !site.slug) {
    return { route: 'modal', reason: 'Choose an address for this site' };
  }

  return { route: 'direct', reason: 'Republishing to the existing address' };
}

/**
 * The destination card's copy.
 *
 * **Not a count.** `selectPublishBaseline()` resolves a version ROW; it does not
 * diff, and there is no diff-summary service. The prototype's "7 changes" is
 * illustrative, and shipping a fabricated number beside a Publish button is the
 * kind of detail a user trusts precisely because it looks computed.
 *
 * @param {{site: object|null, cloudVersion: number|null, publishedVersion: number|null}} ctx
 */
export function describePublishDestination({
  site,
  cloudVersion = null,
  publishedVersion = null,
  status = null
} = {}) {
  // BC-3. `site` has THREE values and the card needs all of them. It used to
  // default to null, so "not read yet" and "the read failed" both rendered as
  // "Not published yet" — telling the owner of a live site it was not live.
  if (status === 'error') {
    return {
      state: 'unreadable',
      label: 'Could not check publish status',
      detail: 'Your site may still be live.'
    };
  }
  if (site === undefined || status === 'loading') {
    return { state: 'loading', label: 'Checking publish status…', detail: null };
  }
  if (!site || !site.slug) {
    return { state: 'unpublished', label: 'Not published yet', detail: null };
  }
  const address = site.baseDomain ? `${site.slug}.${site.baseDomain}` : site.slug;

  // Both numbers have to be real before they can be compared. A null on either
  // side means "unknown", and "unknown" must not render as "Up to date" — that
  // is the one wrong answer, because it tells the user there is nothing to ship.
  if (!Number.isInteger(cloudVersion) || !Number.isInteger(publishedVersion)) {
    return { state: 'unknown', label: address, detail: 'Published' };
  }
  return cloudVersion > publishedVersion
    ? { state: 'behind', label: address, detail: 'Changes since you published' }
    : { state: 'current', label: address, detail: 'Up to date' };
}
